import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { CreateFullPlanRequest, type CreatePlanRequest } from '../types/plan.types';
import {
  getPlanNameOptions,
  getWitnessCompanyOptions,
  getPropertyOptions,
} from '../components/materials/autocomplete-options';
import { useCreatePlanTableStore, defaultPlanValue } from '../stores/create-plan-table.store';
import { createPlan } from '../services/plans.service';
import { convertTo3LineTableData } from '../components/three-line/handsontable-three-line';
import { convert3LineTo1LineTableDataPreserveString } from '../components/three-line/handsontable-three-line.converters';
import {
  adjustRowsWithErrorsAfterInsert,
  adjustRowsWithErrorsAfterDelete,
} from '../components/common/handsontable-helpers';

import {
  convertFromTableData,
  convertFromTableDataPreserveString,
  convertToTableData,
  getDefaultPlanData,
  validateTableData,
} from './use-excel-table';

import type { PlanTableData, RowsWithErrors } from '../components/common/handsontable.types';

import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useSnackbar } from '@/hooks/use-snackbar';
import { useSubmitting } from '@/hooks/use-submitting';
import { SearchOption } from '@/types/search';
import { paths } from '@/config/paths';
import { MESSAGES } from '@/lib/constants';
import { FunctionName, PermissionAction, snackbarType } from '@/lib/enums';

/**
 * Hook for plan registration form
 * Manages form state, validation, data conversion, and submission
 */
export const useRegisterPlan = () => {
  const navigate = useNavigate();
  const { addPlan, createPlanTable } = useCreatePlanTableStore();
  const { setIsLoading } = useLoadingProgress();
  const { setConfirmDialog } = useConfirmDialog();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsSubmitting } = useSubmitting();

  // Form state
  const [tableData, setTableData] = useState<PlanTableData>([]);
  const [planCount, setPlanCount] = useState<number>(10); // Track number of plans to ensure consistency (default: 10 rows)
  const [rowsWithErrors, setRowsWithErrors] = useState<RowsWithErrors>([]);
  const previousTableDataLengthRef = useRef<number>(0); // Track previous table data length to detect row add/delete
  const [selectedProperty, setSelectedProperty] = useState<SearchOption | null>(null);
  const [plantOptions, setPlantOptions] = useState<SearchOption[]>([]);
  const [witnessCompanyOptions, setWitnessCompanyOptions] = useState<SearchOption[]>([]);
  const [propertyOptions, setPropertyOptions] = useState<SearchOption[]>([]);
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const hasLoadedRef = useRef(false); // Prevent double API calls (React StrictMode)
  const getTableDataRef = useRef<(() => PlanTableData) | null>(null);
  const isLayoutSwitchingRef = useRef(false); // Track when switching between layouts
  const isThreeLineLayoutRef = useRef(false); // Track which layout is active

  // Sync tableData from store ONLY on initial load
  // After that, all add/delete operations are handled directly in handlers
  // This prevents overwriting user input when store updates
  useEffect(() => {
    // Only sync on initial load (when tableData is empty and options are loaded)
    // Also require that at least one of plantOptions or witnessCompanyOptions has values
    // to ensure convertToTableData can work properly
    if (
      optionsLoaded &&
      tableData.length === 0 &&
      (plantOptions.length > 0 || witnessCompanyOptions.length > 0)
    ) {
      const newTableDataFromStore = convertToTableData(
        createPlanTable,
        plantOptions,
        witnessCompanyOptions
      );

      // Ensure default 10 empty rows exist so Handsontable can display columns
      if (newTableDataFromStore.length === 0) {
        const defaultRows = Array.from({ length: 10 }, (_, index) => {
          const emptyRow = convertToTableData(
            [{ ...defaultPlanValue }],
            plantOptions,
            witnessCompanyOptions
          )[0];
          emptyRow[0] = index + 1; // Set STT to 1-10
          return emptyRow;
        });
        setTableData(defaultRows);
        setPlanCount(10);
        previousTableDataLengthRef.current = 10;
      } else {
        setTableData(newTableDataFromStore);
        setPlanCount(newTableDataFromStore.length);
        previousTableDataLengthRef.current = newTableDataFromStore.length;
      }
    }
  }, [
    optionsLoaded,
    tableData.length, // Only sync when tableData is empty (initial load)
    plantOptions.length,
    witnessCompanyOptions.length,
    createPlanTable.length,
  ]);

  // Load options on mount
  useEffect(() => {
    if (hasLoadedRef.current) {
      return;
    }

    const loadOptions = async () => {
      hasLoadedRef.current = true;
      setIsLoading(true);
      try {
        const [plants, witnesses, properties] = await Promise.all([
          getPlanNameOptions(FunctionName.SCHEDULE_MANAGEMENT, PermissionAction.UPDATE),
          getWitnessCompanyOptions(FunctionName.SCHEDULE_MANAGEMENT, PermissionAction.UPDATE),
          getPropertyOptions(FunctionName.SCHEDULE_MANAGEMENT, false, PermissionAction.UPDATE), // hasArchived = false
        ]);
        setPlantOptions(plants);
        setWitnessCompanyOptions(witnesses);
        setPropertyOptions(properties);
        setOptionsLoaded(true);
      } catch (error) {
        console.error('[useRegisterPlan] Error loading options:', error);
        setOptionsLoaded(true);
      } finally {
        setIsLoading(false);
      }
    };

    void loadOptions();
  }, [setIsLoading]);

  // Helper: Check if a plan has any non-empty fields
  const isNonEmptyPlan = useCallback((plan: CreatePlanRequest) => {
    return !!(
      plan.placementDate ||
      plan.timeHope ||
      plan.timePossible ||
      plan.placeToPlace ||
      plan.quantity ||
      plan.formulationProduct ||
      plan.witnessName ||
      plan.witnessTel ||
      plan.remarksForClient ||
      plan.remarks
    );
  }, []);

  // Helper: Get current table data with format conversion
  const getCurrentTableData = useCallback((): PlanTableData | null => {
    if (!getTableDataRef.current) {
      return null;
    }

    try {
      const rawData = getTableDataRef.current();

      // Convert to 1-line format if using 3-line layout
      if (isThreeLineLayoutRef.current) {
        return convert3LineTo1LineTableDataPreserveString(
          rawData,
          plantOptions,
          witnessCompanyOptions
        ) as PlanTableData;
      }

      return rawData;
    } catch (error) {
      console.error('[useRegisterPlan] Error getting table data:', error);
      return null;
    }
  }, [plantOptions, witnessCompanyOptions, planCount]);

  // Validation: Check if form is valid for submission
  const isValid = useMemo(() => {
    const haveChanges =
      tableData.filter((row, index) => {
        return (
          JSON.stringify(row) !==
          JSON.stringify(
            getDefaultPlanData(index, defaultPlanValue, plantOptions, witnessCompanyOptions)
          )
        );
      }).length > 0;
    return selectedProperty?.key && tableData.length > 0 && haveChanges;
  }, [selectedProperty, tableData, plantOptions, witnessCompanyOptions]);

  // Validation: Check if at least one valid plan exists
  const hasValidPlans = useCallback(() => {
    if (!tableData || tableData.length === 0) return false;

    const plans = convertFromTableData(tableData, plantOptions, witnessCompanyOptions);
    return plans.some(isNonEmptyPlan);
  }, [tableData, plantOptions, witnessCompanyOptions, isNonEmptyPlan]);

  // Reset function to clear all form data
  const resetAllForms = useCallback(() => {
    // Reset store
    const { resetPlanTable } = useCreatePlanTableStore.getState();
    resetPlanTable();

    // Reset local state
    setTableData([]);
    setPlanCount(10);
    setSelectedProperty(null);
    setRowsWithErrors([]);

    // Reset tableDataRef
    getTableDataRef.current = null;
  }, []);

  // Handle cancel
  const handleCancel = useCallback(() => {
    navigate(paths.plans.path);
  }, [navigate]);

  // Handle submit
  const handleSubmit = useCallback(async () => {
    // Validate property is selected
    if (!selectedProperty?.key) {
      setConfirmDialog({
        title: '現場名称を選択してください。',
      });
      return;
    }

    // Get table data from Handsontable
    const currentTableData = getCurrentTableData();
    if (!currentTableData) {
      setConfirmDialog({
        title: 'テーブルデータを取得できませんでした。',
      });
      return;
    }

    // Validate at least one row exists
    if (currentTableData.length === 0) {
      setConfirmDialog({
        title: '少なくとも1つの予定を追加してください。',
      });
      return;
    }

    const { plans, rowsWithErrors } = validateTableData(
      currentTableData,
      plantOptions,
      witnessCompanyOptions
    );
    if (rowsWithErrors.length > 0) {
      setRowsWithErrors(rowsWithErrors);
      return;
    }

    // Filter out empty plans
    const validPlans = plans.filter(isNonEmptyPlan);

    if (validPlans.length === 0) {
      setConfirmDialog({
        title: '少なくとも1つの予定を入力してください。',
      });
      return;
    }

    // Prepare request
    const request: CreateFullPlanRequest = {
      propertyId: selectedProperty.key as string,
      plans: validPlans,
    };

    try {
      setIsLoading(true);
      await createPlan(request);
      setSnackbarMessage(MESSAGES.REGISTER_SUCCESS, { type: snackbarType.SUCCESS });

      // Set isSubmitting to true FIRST to bypass popup confirm when navigating after successful submit
      // This must be set before reset and navigate to ensure blocker sees it
      setIsSubmitting(true);

      // Reset form before navigation to prevent popup confirm
      resetAllForms();

      // Use requestAnimationFrame to ensure isSubmitting state is updated before navigate
      // This prevents popup confirm from showing after successful submit
      window.requestAnimationFrame(() => {
        // Success: redirect to plans list
        setIsLoading(false);
        navigate(paths.plans.path);
      });
    } catch (error) {
      console.error('[useRegisterPlan] Error creating plan', error);
      setIsLoading(false);
      setConfirmDialog({
        title: '登録に失敗しました。もう一度お試しください。',
      });
    }
  }, [
    selectedProperty,
    getCurrentTableData,
    plantOptions,
    witnessCompanyOptions,
    isNonEmptyPlan,
    navigate,
    setIsLoading,
    setConfirmDialog,
    setSnackbarMessage,
    resetAllForms,
    getTableDataRef,
    setRowsWithErrors,
    setIsSubmitting,
  ]);

  // Handle table data change from HandsontableThreeLine (3-line format)
  // Use preserve string converter to prevent NaN when switching layouts
  // Defined early so it can be used by row operation handlers
  const handleTableDataChange3Line = useCallback(
    (data: PlanTableData) => {
      // Only update if we have valid data to prevent data loss
      if (data && Array.isArray(data) && data.length > 0) {
        // Convert directly from 3-line to 1-line format preserving string values
        // This prevents NaN conversion when switching layouts
        const newTableData = convert3LineTo1LineTableDataPreserveString(
          data,
          plantOptions,
          witnessCompanyOptions
        );
        // Only update if conversion resulted in valid data
        if (newTableData && Array.isArray(newTableData) && newTableData.length > 0) {
          // Always update to the new data (preserves string values)
          setTableData(newTableData);
          // Calculate plan count from 3-line format (3 rows per plan)
          const rowsPerPlan = 3;
          const planCount = Math.floor(data.length / rowsPerPlan);
          setPlanCount(planCount);
        }
      }
    },
    [plantOptions, witnessCompanyOptions]
  );

  // Handle add row at the end
  const handleAddRow = useCallback(() => {
    // Get current data from Handsontable (has user input)
    const currentTableData = getCurrentTableData();
    if (!currentTableData) {
      // Fallback to store action
      addPlan();
      return;
    }

    // Create new empty row (default values)
    const newRow = convertToTableData(
      [{ ...defaultPlanValue }],
      plantOptions,
      witnessCompanyOptions
    )[0];

    // Add new row at the end
    const updatedTableData = [...currentTableData, newRow];

    // Update tableData state
    setTableData(updatedTableData);
    setPlanCount(updatedTableData.length);

    // No need to adjust rowsWithErrors when adding at the end (no existing rows are shifted)

    // Also update store to keep in sync
    addPlan();
  }, [getCurrentTableData, addPlan, plantOptions, witnessCompanyOptions]);

  // Handle add row above specified index
  const handleAddRowAbove = useCallback(
    (index: number) => {
      // Check if we're in 3-line layout
      if (isThreeLineLayoutRef.current && getTableDataRef.current) {
        // For 3-line format: index is row index in 3-line format
        // Get raw 3-line data directly from Handsontable instance (preserves all user input)
        const raw3LineData = getTableDataRef.current();
        if (!raw3LineData || raw3LineData.length === 0) {
          // Fallback to store action
          const planIndex = Math.floor(index / 3);
          const { addPlanAbove } = useCreatePlanTableStore.getState();
          addPlanAbove(planIndex);
          return;
        }

        // Create new empty plan (3 rows) in 3-line format
        const newPlan = convertTo3LineTableData(
          [{ ...defaultPlanValue }],
          plantOptions,
          witnessCompanyOptions
        );

        // Insert 3 rows at correct position in 3-line data
        const updated3LineData = [...raw3LineData];
        updated3LineData.splice(index, 0, ...newPlan);

        // Update state using handleTableDataChange3Line to preserve string values
        // This ensures proper conversion and triggers Handsontable update via props
        handleTableDataChange3Line(updated3LineData);

        // Adjust validation errors: shift all errors at/after the insert position
        const planIndex = Math.floor(index / 3);
        setRowsWithErrors((prevErrors) => adjustRowsWithErrorsAfterInsert(prevErrors, planIndex));

        // Also update store to keep in sync
        const { addPlanAbove } = useCreatePlanTableStore.getState();
        addPlanAbove(planIndex);
        return;
      }

      // For 1-line format: index is row index in 1-line format
      // Get current data from Handsontable (has user input)
      const currentTableData = getCurrentTableData();
      if (!currentTableData) {
        // Fallback to store action
        const { addPlanAbove } = useCreatePlanTableStore.getState();
        addPlanAbove(index);
        return;
      }

      // Create new empty row (default values)
      const newRow = convertToTableData(
        [{ ...defaultPlanValue }],
        plantOptions,
        witnessCompanyOptions
      )[0];
      // Update STT will be handled after insert

      // Insert new row at correct position in current data
      const updatedTableData = [...currentTableData];
      updatedTableData.splice(index, 0, newRow);

      // Update tableData state
      setTableData(updatedTableData);
      setPlanCount(updatedTableData.length);

      // Adjust validation errors: shift all errors at/after the insert position
      setRowsWithErrors((prevErrors) => adjustRowsWithErrorsAfterInsert(prevErrors, index));

      // Also update store to keep in sync
      const { addPlanAbove } = useCreatePlanTableStore.getState();
      addPlanAbove(index);
    },
    [
      getCurrentTableData,
      plantOptions,
      witnessCompanyOptions,
      getTableDataRef,
      handleTableDataChange3Line,
    ]
  );

  // Handle add row below specified index
  const handleAddRowBelow = useCallback(
    (index: number) => {
      // Check if we're in 3-line layout
      if (isThreeLineLayoutRef.current) {
        // Handle case when getTableDataRef is null (fallback to store action)
        if (!getTableDataRef.current) {
          // For 3-line format: convert row index to plan index
          const planIndex = Math.floor(index / 3);
          const { addPlanBelow } = useCreatePlanTableStore.getState();
          addPlanBelow(planIndex);
          return;
        }

        // For 3-line format: index is already the start row of the next plan (from getRowIndexForAddBelow)
        // Get raw 3-line data directly from Handsontable instance (preserves all user input)
        const raw3LineData = getTableDataRef.current();
        if (!raw3LineData || raw3LineData.length === 0) {
          // Fallback to store action
          const planIndex = Math.floor(index / 3);
          const { addPlanBelow } = useCreatePlanTableStore.getState();
          addPlanBelow(planIndex);
          return;
        }

        // Create new empty plan (3 rows) in 3-line format
        const newPlan = convertTo3LineTableData(
          [{ ...defaultPlanValue }],
          plantOptions,
          witnessCompanyOptions
        );

        // Insert 3 rows at correct position in 3-line data
        // index is already the start row of the next plan, so insert at index (not index + 1)
        const updated3LineData = [...raw3LineData];
        updated3LineData.splice(index, 0, ...newPlan);

        // Update state using handleTableDataChange3Line to preserve string values
        // This ensures proper conversion and triggers Handsontable update via props
        handleTableDataChange3Line(updated3LineData);

        // Adjust validation errors: shift all errors at/after the insert position
        // index is the start row of the next plan, so planIndex is where the new plan is inserted
        // All plans at planIndex and after need their error indices shifted +1
        const planIndex = Math.floor(index / 3);
        setRowsWithErrors((prevErrors) => adjustRowsWithErrorsAfterInsert(prevErrors, planIndex));

        // Also update store to keep in sync
        const { addPlanBelow } = useCreatePlanTableStore.getState();
        addPlanBelow(planIndex);
        return;
      }

      // For 1-line format: index is row index in 1-line format
      // Get current data from Handsontable (has user input)
      const currentTableData = getCurrentTableData();
      if (!currentTableData) {
        // Fallback to store action
        const { addPlanBelow } = useCreatePlanTableStore.getState();
        addPlanBelow(index);
        return;
      }

      // Create new empty row (default values)
      const newRow = convertToTableData(
        [{ ...defaultPlanValue }],
        plantOptions,
        witnessCompanyOptions
      )[0];

      // Insert new row at correct position in current data
      const updatedTableData = [...currentTableData];
      updatedTableData.splice(index + 1, 0, newRow);

      // Update tableData state
      setTableData(updatedTableData);
      setPlanCount(updatedTableData.length);

      // Adjust validation errors: shift all errors after the insert position
      setRowsWithErrors((prevErrors) => adjustRowsWithErrorsAfterInsert(prevErrors, index + 1));

      // Also update store to keep in sync
      const { addPlanBelow } = useCreatePlanTableStore.getState();
      addPlanBelow(index);
    },
    [
      getCurrentTableData,
      plantOptions,
      witnessCompanyOptions,
      getTableDataRef,
      handleTableDataChange3Line,
    ]
  );

  const handleDeleteRows = useCallback(
    (indices: number[]) => {
      if (!Array.isArray(indices) || indices.length === 0) {
        return;
      }

      const deleteIndices = Array.from(new Set(indices))
        .filter((index) => Number.isInteger(index) && index >= 0)
        .sort((a, b) => b - a);

      if (deleteIndices.length === 0) {
        return;
      }

      // Check if we're in 3-line layout
      if (isThreeLineLayoutRef.current && getTableDataRef.current) {
        // For 3-line format: indices are plan indices
        const raw3LineData = getTableDataRef.current();
        if (!raw3LineData || raw3LineData.length === 0) {
          const { deletePlan } = useCreatePlanTableStore.getState();
          deleteIndices.forEach((index) => deletePlan(index));
          return;
        }

        const rowsPerPlan = 3;
        const updated3LineData = [...raw3LineData];

        deleteIndices.forEach((planIndex) => {
          const startRowIndex = planIndex * rowsPerPlan;
          if (startRowIndex >= 0 && startRowIndex < updated3LineData.length) {
            updated3LineData.splice(startRowIndex, rowsPerPlan);
          }
        });

        // Ensure at least one plan (3 rows) exists
        if (updated3LineData.length === 0) {
          const newPlan = convertTo3LineTableData(
            [{ ...defaultPlanValue }],
            plantOptions,
            witnessCompanyOptions
          );
          updated3LineData.push(...newPlan);
        }

        handleTableDataChange3Line(updated3LineData);

        setRowsWithErrors((prevErrors) => {
          return deleteIndices.reduce(
            (errors, index) => adjustRowsWithErrorsAfterDelete(errors, index),
            prevErrors
          );
        });

        const { deletePlan } = useCreatePlanTableStore.getState();
        deleteIndices.forEach((index) => deletePlan(index));
        return;
      }

      // For 1-line format: indices are row indices
      const currentTableData = getCurrentTableData();
      if (!currentTableData) {
        const { deletePlan } = useCreatePlanTableStore.getState();
        deleteIndices.forEach((index) => deletePlan(index));
        return;
      }

      const updatedTableData = [...currentTableData];
      deleteIndices.forEach((index) => {
        if (index >= 0 && index < updatedTableData.length) {
          updatedTableData.splice(index, 1);
        }
      });

      // Ensure at least one row exists
      if (updatedTableData.length === 0) {
        const newRow = convertToTableData(
          [{ ...defaultPlanValue }],
          plantOptions,
          witnessCompanyOptions
        )[0];
        updatedTableData.push(newRow);
      }

      setTableData(updatedTableData);
      setPlanCount(updatedTableData.length);

      setRowsWithErrors((prevErrors) => {
        return deleteIndices.reduce(
          (errors, index) => adjustRowsWithErrorsAfterDelete(errors, index),
          prevErrors
        );
      });

      const { deletePlan } = useCreatePlanTableStore.getState();
      deleteIndices.forEach((index) => deletePlan(index));
    },
    [
      getCurrentTableData,
      plantOptions,
      witnessCompanyOptions,
      getTableDataRef,
      handleTableDataChange3Line,
    ]
  );

  // Register getTableData function from Handsontable (1-line layout)
  const registerGetTableData = useCallback((getData: () => PlanTableData) => {
    getTableDataRef.current = getData;
    isThreeLineLayoutRef.current = false; // Mark as 1-line layout
  }, []);

  // Update table data when Handsontable data changes
  const handleTableDataChange = useCallback((data: PlanTableData) => {
    // Only update if we have valid data to prevent data loss
    if (data && Array.isArray(data) && data.length > 0) {
      const currentLength = data.length;
      const previousLength = previousTableDataLengthRef.current;

      // Safety net: Remove validation errors for rows that no longer exist
      // This handles edge cases where Handsontable's built-in operations are used
      // The handlers (handleAddRowAbove/handleAddRowBelow/handleDeleteRows) should have already
      // adjusted errors, but this ensures we don't have errors for non-existent rows
      if (previousLength > 0 && currentLength < previousLength) {
        setRowsWithErrors((prevErrors) => {
          return prevErrors.filter((error) => error.rowIndex < currentLength);
        });
      }

      setTableData(data);
      setPlanCount(currentLength); // Update plan count (1-line: 1 row = 1 plan)
      previousTableDataLengthRef.current = currentLength;
    }
  }, []);

  // Convert tableData to 3-line format for HandsontableThreeLine
  // Use preserve string converter to prevent NaN when switching layouts
  const tableData3Line = useMemo(() => {
    if (tableData.length === 0) {
      return [];
    }
    // Use preserve string converter to keep quantity as string when switching layouts
    const plans = convertFromTableDataPreserveString(
      tableData,
      plantOptions,
      witnessCompanyOptions
    );
    const result = convertTo3LineTableData(plans, plantOptions, witnessCompanyOptions);
    return result;
  }, [tableData, plantOptions, witnessCompanyOptions]);

  // Register getTableData function from HandsontableThreeLine (3-line layout)
  const registerGetTableData3Line = useCallback((getData: () => PlanTableData) => {
    // Store the raw getData function - conversion will happen in handleSubmit
    getTableDataRef.current = getData;
    isThreeLineLayoutRef.current = true; // Mark as 3-line layout
  }, []);

  // Function to set layout switching flag
  const setLayoutSwitching = useCallback((isSwitching: boolean) => {
    isLayoutSwitchingRef.current = isSwitching;
    // Don't reset flag here - let the page component handle the timing
    // This prevents duplicate timers and ensures proper synchronization
  }, []);

  return {
    // State
    tableData,
    tableData3Line,
    selectedProperty,
    setSelectedProperty,
    plantOptions,
    witnessCompanyOptions,
    propertyOptions,
    optionsLoaded,
    isValid,
    hasValidPlans,

    // Actions
    handleCancel,
    handleSubmit,
    handleAddRow,
    handleAddRowAbove,
    handleAddRowBelow,
    handleDeleteRows,
    registerGetTableData,
    handleTableDataChange,
    registerGetTableData3Line,
    handleTableDataChange3Line,
    setLayoutSwitching,
    rowsWithErrors,
    getTableDataRef,
    setRowsWithErrors,
    resetAllForms,
  };
};
